/**
 * Regenerating a work order: how long to wait, what to say while waiting, and
 * what a failed-looking regenerate actually was (Issue #1413).
 *
 * The sibling of `pages/field/wo-submit-retry.ts` (#1421), for the office side
 * of the same defect. `POST /office-review/jobs/:jobId/refill-work-order`
 * re-fills the vendor's PDF, archives the superseded one and stores the
 * replacement — all inside one request — while the app aborted every request
 * at 30 seconds. Corbin hit ⟳ on a Cintas job and got two red toasts over a
 * refill that had SUCCEEDED: aborting a `fetch` does not cancel the Express
 * handler, so the new PDF landed while the screen said it hadn't, and the
 * re-click that invited ran the entire fill a second time.
 *
 * #1413 already removed the dominant cost (a regenerate no longer has a vision
 * model read the PDF back). That made the endpoint fast; it did not make the
 * screen honest, because the 30s cliff and the "any failure is a failure"
 * error path were both still there. This module is that half.
 *
 * Kept pure and outside the four screens that regenerate, so the rules are
 * provable without rendering any of them — and so all four say the same thing.
 * Four call sites is exactly how wording drifts (#658 is the same lesson about
 * the four buttons themselves).
 */

/**
 * How long a regenerate may run before the client gives up.
 *
 * The 30s default is shorter than this endpoint's honest cost: it downloads
 * the base document and every signature from storage, fills, uploads, and
 * writes. Cutting that off never made it faster — it just told the office it
 * failed. Matches `WO_SUBMIT_TIMEOUT_MS` (#1421) and the upload budget rather
 * than inventing a third number.
 */
export const WO_REGENERATE_TIMEOUT_MS = 180_000;

/** Past this, a regenerate is slow enough that the row should say so. */
export const WO_REGENERATE_SLOW_AFTER_MS = 30_000;

/** `0:06`, `0:41`, `2:07` — elapsed time as a person reads a stopwatch. */
export function formatElapsed(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * What the WO row says while a regenerate is running.
 *
 * The point of the second line is that 30 seconds stops being a cliff: at 0:41
 * the old screen had already declared failure, and this one is still honestly
 * reporting work in progress.
 */
export function regenerateProgressLine(elapsedMs: number): { text: string; slow: boolean } {
    const clock = formatElapsed(elapsedMs);
    if (elapsedMs < WO_REGENERATE_SLOW_AFTER_MS) {
        return { text: `Regenerating… ${clock} — re-filling on the job's template.`, slow: false };
    }
    return { text: `Still going — ${clock}. Safe to leave this screen; it finishes on its own.`, slow: true };
}

/** What actually happened, once the client has re-checked with the server. */
export type RegenerateOutcome =
    | /** It went through after all — a new filled PDF is on the job. */ "landed"
    | /** We reached the server and nothing new landed. Safe to try again. */ "not-run"
    | /** We could not ask, so we do not know. Safe to try again. */ "unconfirmed"
    | /** The server answered and refused. Something must change first. */ "rejected";

/**
 * Decide what a failed-looking regenerate really was.
 *
 * The load-bearing case is the first one: a request that timed out, whose job
 * now carries a filled PDF that was not there before, DID regenerate. Calling
 * that a failure is the whole bug. A timeout with no new PDF is honestly
 * "not-run"; a re-check that could not reach the server proves nothing, and
 * says so rather than picking whichever answer sounds tidier.
 *
 * A retry is safe in every non-landed case — a regenerate archives the old PDF
 * and stores a new one, so running it twice ends in the same place as running
 * it once (it just does the work twice). That is why "unconfirmed" can still
 * offer the button; it is not why it may claim the work didn't happen.
 */
export function classifyRegenerateFailure(opts: {
    /** True when the request never got an answer (timeout / offline / DNS). */
    networkError: boolean;
    /** Newest `work_order_filled` attachment id on the job BEFORE we started. */
    filledIdBefore?: string | null;
    /** Newest `work_order_filled` attachment id from the re-check. */
    filledIdAfter?: string | null;
    /** True when the re-check itself failed, so the ids prove nothing. */
    recheckFailed?: boolean;
}): RegenerateOutcome {
    if (!opts.networkError) return "rejected";
    if (opts.recheckFailed) return "unconfirmed";
    const after = opts.filledIdAfter ?? null;
    if (after && after !== (opts.filledIdBefore ?? null)) return "landed";
    return "not-run";
}

/** The one place the office-facing wording for each outcome lives. */
export function regenerateFailureMessage(outcome: Exclude<RegenerateOutcome, "landed">): {
    title: string;
    description?: string;
} {
    if (outcome === "not-run") {
        return {
            title: "The work order wasn't regenerated.",
            description: "Nothing changed on the job — try again.",
        };
    }
    if (outcome === "unconfirmed") {
        return {
            title: "Couldn't confirm whether that regenerated.",
            description: "Refresh to see where it landed, or try again — a second run replaces the same PDF.",
        };
    }
    return { title: "Couldn't regenerate the work order.", description: undefined };
}

/** Newest-first attachment lists are the shape every caller already has. */
export function newestFilledId(attachments: Array<{ id: string; created_at?: string }> | undefined): string | null {
    if (!attachments?.length) return null;
    const sorted = [...attachments].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    return sorted[0]?.id ?? null;
}
